import { ReactNode } from 'react'
import { KojinResult } from 'interface/kojin'
import { KojinAnnotateResult } from 'interface/kojin/annotate'

import { Avatar, Badge } from '@mui/material'

import { DontIcon, NoCareIcon } from 'shared/icons'

import { styled } from '@mui/material/styles'

const sizeToPx = (size?: 'large' | 'medium' | 'small') => (
  size === 'large' ? '80px' :
  size === 'medium' ? '50px' :
  size === 'small' ? '40px' : '40px'
)

const SourceBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
  },
}))

const StyledLabelBadge = (props: { backgroundColor: string }) =>
  styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
      backgroundColor: props.backgroundColor,
      color: '#fff',
      right: '50%',
      bottom: 0,
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    },
  }))

const PercentBadge = StyledLabelBadge({ backgroundColor: '#91cc75' })

const AnnotateBadge = (props: { annotate: 'dont' | 'nocare' }) =>
  styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
      right: '20%',
      bottom: '10%',
      color: (
        props.annotate === 'dont' ? theme.palette.warning.main :
        props.annotate === 'nocare' ? theme.palette.error.main : ''
      ),
    },
  }))

const DontAnnotatedBadge = AnnotateBadge({ annotate: 'dont' })
const NoCareAnnotatedBadge = AnnotateBadge({ annotate: 'nocare' })

const RateBadge = (props: {
  kojin?: KojinResult
  annotate?: KojinAnnotateResult
  children: ReactNode
}) => {
  const { kojin, annotate, children } = props

  if (kojin?.target) {
    if (kojin.target.sourceType === 0)
      return (
        <SourceBadge
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          variant="dot"
          color="primary"
        >
          {children}
        </SourceBadge>
      )
    if ((annotate || kojin.annotate)?.score === 1)
      return (
        <DontAnnotatedBadge
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          badgeContent={<DontIcon />}
        >
          {children}
        </DontAnnotatedBadge>
      )
    if ((annotate || kojin.annotate)?.score === 2)
      return (
        <NoCareAnnotatedBadge
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          badgeContent={<NoCareIcon />}
        >
          {children}
        </NoCareAnnotatedBadge>
      )
    return (
      <PercentBadge
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        badgeContent={`${Math.ceil((kojin.target?.rate ?? 0) * 100)}%`}
      >
        {children}
      </PercentBadge>
    )
  }
  return <>{children}</>
}

const SpecificImageAvatar = (props: {
  name?: string | null
  fileName: string
  size?: 'large' | 'medium' | 'small'
}) => {
  const { name, fileName, size } = props
  return (
    <Avatar
      alt={name ?? 'noname'}
      src={`/avatar/${fileName}`}
      sx={{
        boxShadow: '0 0 3px rgba(0,0,0,0.3)',
        border: 'solid 2px #fff',
        width: sizeToPx(size),
        height: sizeToPx(size),
        margin: 'auto',
      }}
    />
  )
}
export const KojinAvatar = (props: {
  kojin?: KojinResult
  rank?: number
  globalRank?: number
  annotate?: KojinAnnotateResult
  size?: 'large' | 'medium' | 'small'
}) => {
  const { kojin, annotate, size } = props
  return (
    <RateBadge kojin={kojin} annotate={annotate}>
      {!kojin?.kojinCode ? (
        <SpecificImageAvatar
          name={kojin?.isiName}
          fileName="kojinNoImage_nomatch.png"
          size={size}
        />
      ) : kojin.actor?.photoLink ? (
        <img
          src={kojin.actor.photoLink}
          alt={kojin.isiName ?? '医師'}
          style={{
            maxWidth: sizeToPx(size),
            maxHeight: sizeToPx(size),
            boxShadow: '0px 0px 3px rgba(0,0,0,0.5)',
            border: 'solid 1px #fff',
            borderRadius: '4px',
          }}
        />
      ) : kojin.seibetuCode === '2' ? (
        <SpecificImageAvatar
          name={kojin?.isiName}
          fileName="kojinNoImage_Female.png"
          size={size}
        />
      ) : kojin.seibetuCode === '1' ? (
        <SpecificImageAvatar
          name={kojin?.isiName}
          fileName="kojinNoImage_Male.png"
          size={size}
        />
      ) : (
        <SpecificImageAvatar
          name={kojin?.isiName}
          fileName="kojinNoImage_unknwon.png"
          size={size}
        />
      )}
    </RateBadge>
  )
}
